import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { resetUsers } from 'services';
import { useLazyGetTicketsAIAgentQuery } from 'services';
import { AppDispatch } from 'store';
import { FindAllTicketAIAgentDto } from 'types/dto/ticket';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'utils';
const pageSize = DEFAULT_PAGE_SIZE;
export function useLazyTicketsAgent() {
  const dispatch = useDispatch<AppDispatch>();

  const [trigger, { data: dataResponse, isLoading, isFetching, originalArgs, endpointName }] =
    useLazyGetTicketsAIAgentQuery();

  const { rows, count } = dataResponse?.data || {};
  const hasMore = Boolean(rows && count && rows.length < count);

  const groupedByStatusId = useMemo(() => groupTicketsByStatusId(rows || []), [rows]);

  const handleLoadMore = useCallback(() => {
    if (hasMore)
      trigger({
        ...originalArgs,
        pageSize,
        pageIndex: originalArgs?.pageIndex ? originalArgs?.pageIndex + 1 : DEFAULT_PAGE_INDEX,
        isMore: true
      });
  }, [hasMore, originalArgs]);

  const fetchData = useCallback((value?: Partial<FindAllTicketAIAgentDto>) => {
    trigger({
      pageIndex: 1,
      pageSize,
      isMore: true,
      ...originalArgs,
      ...value
    });
  }, []);

  const clearData = () => {
    if (endpointName) dispatch(resetUsers(endpointName));
  };

  return {
    data: rows || [],
    count: count || 0,
    isFetching,
    isLoading,
    handleLoadMore,
    hasMore,
    fetchData,
    endpointName,
    clearData,
    groupedByStatusId
  };
}

export function groupTicketsByStatusId<T extends { statusId?: string | number }>(tickets: T[]): Record<string, T[]> {
  return tickets.reduce(
    (acc, ticket) => {
      const key = ticket.statusId != null ? String(ticket.statusId) : 'undefined';
      if (!acc[key]) acc[key] = [];
      acc[key].push(ticket);
      return acc;
    },
    {} as Record<string, T[]>
  );
}
